import { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  ctaClickEvent,
  formAbandonmentEvent,
  formFieldErrorEvent,
  formIntermediateEvent,
  formStartEvent,
  formSubmissionFailEvent,
  formSuccessEvent,
} from "../services/adobeDataLayer.js";
import {
  formatAndValidateE164,
  hashValue,
  normalizeEmail,
  setValuesInLocalStorange,
} from "../services/utils.js";

import queryString from "query-string";
import { useCookies } from "react-cookie";
import "react-datepicker/dist/react-datepicker.css";

import { callLSQLapp, encryptBody, getLsqLeadBody } from "../services/lsq.js";

function FormComponent() {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  function showToast(type, message) {
    const toast = document.createElement("div");
    toast.className = `toast ${type}`;
    toast.textContent = message;

    document.getElementById("toastContainer").appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  }

  // state
  const [loanAmount, setLoanAmount] = useState(20000); // default value
  const [loanAmountInput, setLoanAmountInput] = useState("");

  // keep in sync when loanAmount changes (e.g. slider)
  useEffect(() => {
    setLoanAmountInput(loanAmount === "" ? "" : formatIndianNumber(loanAmount));
  }, [loanAmount]);

  const [showSendOtp, setShowSendOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(60);
  const [isVisible, setIsVisible] = useState(true);

  const [token, setToken] = useState("");
  const [fbp, setFbp] = useState("");
  const [fbc, setFbc] = useState("");
  const [cookies, setCookie] = useCookies();
  const [utmDetails, setUtmDetails] = useState({});
  const [disableApplyBtn, setApplyBtnDisable] = useState(false);

  const inputWrapperRef = useRef(null);
  const hasTriggeredFormStart = useRef(false);
  const lastAccessedFieldRef = useRef("");
  const [hasAbandoned, setHasAbandoned] = useState(false);
  const [otpSentCount, setOtpSentCount] = useState(0);
  const firstOtpSentTimeRef = useRef(null);
  const otpRef = useRef(null);
  const OTP_LIMIT = 3;
  const OTP_WINDOW = 10 * 60 * 1000;
  const maxAmount = 2000000;
  const percentage = (loanAmount / maxAmount) * 100;

  const mobileValue = watch("mobile");

  useEffect(() => {
    setValue("loanAmount", loanAmount);
  }, [loanAmount, setValue]);

  useEffect(() => {
    // if otpVerified is true, do not focus (can't focus disabled field)
    if (!otpVerified) {
      // setFocus waits for RNHF internals and is reliable
      setFocus("fullName");
    }
  }, [otpVerified, setFocus]);

  useEffect(() => {
    if (/^\d{10}$/.test(mobileValue)) {
      setShowSendOtp(true);
    } else {
      setShowSendOtp(false);
    }
  }, [mobileValue]);

  // Timer countdown
  useEffect(() => {
    if (otpSent && timer > 0 && !otpVerified) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
        if (timer <= 1) {
          setIsVisible(!isVisible);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpSent, timer, otpVerified]);

  //NEW: Auto-verify OTP when 6 digits are entered
  useEffect(() => {
    if (otpSent && !otpVerified && otp && otp.length === 6) {
      handleVerifyOtp(); // same function you used on the Verify OTP button
    }
  }, [otp, otpSent, otpVerified]);

  //Last Accessed Field
  useEffect(() => {
    const resetAccessField = () => {
      lastAccessedFieldRef.current = "";
    };
    window.addEventListener("beforeunload", resetAccessField);
    return () => window.removeEventListener("beforeunload", resetAccessField);
  }, []);

  // React event for input fields
  const handleChange = async (e) => {
    const fieldName = e.target.name || "unknown";
    lastAccessedFieldRef.current = fieldName;
    if (!hasTriggeredFormStart.current) {
      formStartEvent("plcc lead form", "lead generation", "details");
      hasTriggeredFormStart.current = true;
    }
    setHasAbandoned(false);
  };

  // Outside click handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const wrapper = inputWrapperRef.current;
      if (
        wrapper &&
        !wrapper.contains(event.target) &&
        hasTriggeredFormStart.current &&
        !hasAbandoned
      ) {
        const lastField = lastAccessedFieldRef.current;
        formAbandonmentEvent(
          "plcc lead form",
          "lead generation",
          lastField,
          "details"
        );
        setHasAbandoned(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasAbandoned]);

  // set Fbp, Fbc, UTM parameters values
  useEffect(() => {
    let retryCount = 0; // Initialize retry counter
    const maxRetries = 5; // Maximum number of retries
    const retryInterval = 3000; // Retry interval in milliseconds

    const fetchCookies = () => {
      const getCookieValue = (cookieName) => {
        return (
          document.cookie
            .split("; ")
            .find((row) => row.startsWith(cookieName))
            ?.split("=")[1] || ""
        );
      };

      const fbpValue = getCookieValue("_fbp");
      const fbcValue = getCookieValue("_fbc");

      // Update state if values are found
      if (fbpValue) setFbp(fbpValue);
      if (fbcValue) setFbc(fbcValue);

      // Retry logic
      if ((!fbpValue || !fbcValue) && retryCount < maxRetries) {
        retryCount++;
        setTimeout(fetchCookies, retryInterval);
      }
    };

    fetchCookies();

    // set UTM parameters
    const getUTMDetails = queryString.parse(window.location.search);

    setUtmDetails(getUTMDetails);
  }, []);

  //to handle form error events
  const handleFormFieldErrorEvent = (errorData) => {
    formFieldErrorEvent(errorData);
  };

  useEffect(() => {
    const handleErrorEvent = (e) => {
      if (hasTriggeredFormStart.current) {
        handleFormFieldErrorEvent(e.detail);
      }
    };

    window.addEventListener("formErrorEvent", handleErrorEvent);

    return () => {
      window.removeEventListener("formErrorEvent", handleErrorEvent);
    };
  }, []);

  function triggerFormErrorEvent(errors) {
    const flatErrors = {};

    Object.keys(errors).forEach((key) => {
      if (errors[key]?.message) {
        flatErrors[key] = errors[key].message;
      }
    });

    if (Object.keys(flatErrors).length === 0) return; // no error, no event

    const event = new CustomEvent("formErrorEvent", {
      detail: flatErrors,
    });

    window.dispatchEvent(event);
  }

  const onError = async (errors) => {
    if (!hasTriggeredFormStart.current) {
      console.log("Calling formStartEvent");
      await formStartEvent("plcc lead form", "lead generation", "details");

      hasTriggeredFormStart.current = true;
    }

    console.log("Calling triggerFormErrorEvent");
    triggerFormErrorEvent(errors);
  };

  const handleFieldFocus = (fieldName) => {
    lastAccessedFieldRef.current = fieldName;
  };

  const handleSendOtp = async (source) => {
    ctaClickEvent(`otp:${source}`, "lead form", "button", 0, "na");

    if (!mobileValue) return;
    if (mobileValue.length !== 10) return;

    const now = Date.now();

    // Reset window if 10 mins passed since first OTP sent
    if (
      !firstOtpSentTimeRef.current ||
      now - firstOtpSentTimeRef.current > OTP_WINDOW
    ) {
      firstOtpSentTimeRef.current = now;
      setOtpSentCount(0);
    }

    if (otpSentCount >= OTP_LIMIT) {
      showToast(
        "error",
        "You have reached the maximum of 3 OTP sends within 10 minutes."
      );
      return;
    }

    try {
      let payload = { mobile: mobileValue, messageType: "otpGeneral" };
      let param = { otp: "send", productType: "CC" };
      const res = await callLSQLapp(payload, "OTP", param);

      if (res.data.message.response.sent) {
        // Update count after successful send
        setOtpSentCount((count) => count + 1);

        // Trigger analytics events
        formIntermediateEvent("send", "otp");

        showToast("success", "An OTP has been sent to your Mobile number.");
        setTimeout(() => {
          otpRef.current?.focus();
        }, 50);
        setOtpVerified(false);
        setTimer(60);
        setOtpSent(true);
        setIsVisible(!isVisible);
      }
    } catch (error) {
      showToast("error", "Error occurred while generating OTP.");
    }
  };

  // const handleSendOtp = async () => {
  //   if (!mobileValue) return;
  //   if (mobileValue.length === 10) {
  //     try {
  //       const res = await onSendOtp(mobileValue);
  //       if (res.data.message.response.sent) {
  //         showToast("success", "An OTP has been sent to your Mobile number.");
  //         setOtpVerified(false);
  //         setTimer(60);
  //         setOtpSent(true);
  //         setIsVisible(!isVisible);
  //       }
  //     } catch (error) {
  //       showToast("error", "Error occurred while generating OTP.");
  //     }
  //   }
  // };

  const handleVerifyOtp = async () => {
    ctaClickEvent("otp:verify", "lead form", "button", 0, "na");
    if (!mobileValue) {
      showToast("error", "Please enter mobile number");
      return;
    }
    if (otp.length === 6) {
      try {
        let payload = { mobile: mobileValue, otp: otp };
        let param = { otp: "verify", productType: "CC" };
        const otpVerifyResponse = await callLSQLapp(payload, "OTP", param);
        if (
          otpVerifyResponse.data.message ===
          "Failure Occured, please check logs"
        ) {
          showToast("error", "Incorrect OTP");
          //alert("Please enter valid OTP");
        } else {
          setOtpVerified(true);
          setTimer(0);
          formIntermediateEvent("verified", "otp");
          showToast("success", "OTP verified.");
          setFocus("email");
          const tokenValue =
            otpVerifyResponse.data.message.response.dataSent.UniqueID;
          setToken(tokenValue);
          setIsVisible(!isVisible);
        }
      } catch (error) {
        showToast("error", "Error verifying OTP.");
        //alert("Error verifying OTP.");
      }
    }
  };

  // const handleVerifyOtp = async () => {
  //   if (!mobileValue) {
  //     showToast("error", "Please enter mobile number");
  //     return;
  //   }
  //   if (otp.length === 6) {
  //     try {
  //       const res = await onVerifyOtp(mobileValue, otp);
  //       if (res.data.message === "Failure Occured, please check logs") {
  //         showToast("error", "Please enter valid OTP");
  //       } else {
  //         setOtpVerified(true);
  //         setTimer(0);
  //         showToast("success", "OTP verified.");
  //         setIsVisible(!isVisible);
  //       }
  //     } catch (error) {
  //       showToast("error", "Error verifying OTP.");
  //     }
  //   }
  // };

  // const handleReset = () => {
  //   if (hasTriggeredFormStart.current) {
  //     ctaClickEvent("Reset", "lead form", "button", 0, "na");
  //   }

  //   reset();
  //   setOtp("");
  //   setOtpSent(false);
  //   setOtpVerified(false);
  //   setTimer(60);
  //   setShowSendOtp(false);
  //   setLoanAmount(60000);
  // };

  const handleSliderChange = (e) => {
    setLoanAmount(Number(e.target.value));
  };

  const handleNumberChange = (e) => {
    let raw = e.target.value.replace(/₹/g, "").replace(/,/g, "").trim();
    let val = Number(raw);
    if (isNaN(val)) return;

    val = Math.min(Math.max(val, 0), 2000000);
    setLoanAmount(val);
  };

  const formatIndianNumber = (num) => {
    return "₹ " + num.toLocaleString("en-IN");
  };

  const onSubmit = async (data) => {
    if (!otpSent && mobileValue.length === 10) {
      showToast("error", "Click on Send OTP to continue.");
      return;
    } else if (!otp) {
      showToast("error", "Please enter OTP to continue.");
      return;
    } else if (!otpVerified) {
      showToast("error", "Please verify OTP to continue.");
      return;
    }

    if (otpVerified) {
      setApplyBtnDisable(true);
    }
    // additional static fields
    data.token = token;
    data.Product = "PLCC";
    data.ModeOfLead = "landing-page";
    data.CampaignId = "819038";
    data.CompleteUrl = window.location.href;
    data.FBPixelID = fbp;
    data.FBClickID = fbc;
    data.IsAuthorized = "Yes";
    data.pageType = "plcc"; //unique Identifier
    data.User_Agent = navigator.userAgent;
    data.otp = otp;
    data.Eligible = "Yes";
    data.loanAmount = loanAmount;

    // get utm parameteres values
    const utmParamsObj = queryString.parse(window.location.search);
    // console.log(utmParamsObj);
    for (const [key, value] of Object.entries(utmParamsObj)) {
      // console.log("key", { key: value });
      data[key] = value;
    }

    let payload = {
      mobile: data.mobile,
      otp: data.otp,
      formdata: getLsqLeadBody(data),
    };
    let param = { productType: "CC" };

    console.log("lead create payload: ", payload);

    const encryptedPayload = await encryptBody(payload);
    let createLeadResp = await callLSQLapp(encryptedPayload, "CREATE", param);
    if (createLeadResp.data.message !== "Failure occured , please check logs") {
      let leadid = createLeadResp.data.message.Data.LeadApiCalls.Message.Id;
      console.log("leadID", leadid);

      if (data.mobile && data.email) {
        const formattedMobile = formatAndValidateE164(data.mobile);
        const formattedEmail = normalizeEmail(data.email);
        const hashedMobile = await hashValue(formattedMobile);
        const hashedEmail = await hashValue(formattedEmail);
        setValuesInLocalStorange("userData", hashedEmail, hashedMobile);
        console.log(localStorage.getItem("userData"), "userData");
      }

      // Call form success event
      formSuccessEvent(
        "plcc lead form",
        { otp: otp, loanAmount: data.loanAmount },
        "lead generation",
        "thank you",
        true
      );

      // Navigate to thank you page
      navigate(`/thankyou?id=${leadid}`);
    } else {
      formSubmissionFailEvent();
      setApplyBtnDisable(false);
      showToast("error", "Error Occured, Please try again after sometime");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} id="myForm">
      <h3>Fill out the form to get started</h3>
      <div className="px-3 py-2">
        <div className="input_wrapper">
          <input
            type="text"
            id="name"
            placeholder="Full Name (As per PAN)"
            // disabled={otpVerified}
            {...register("fullName", {
              required: "Full name is required",
              pattern: {
                value: /^[a-zA-Z\s]+$/,
                message: "Only alphabets and spaces allowed",
              },
            })}
            onInput={(e) => {
              handleChange(e);
              e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
            }}
            onFocus={() => handleFieldFocus("fullName")}
          />

          {errors.fullName && (
            <span className="error">{errors.fullName.message}</span>
          )}
        </div>

        {/* Mobile Number */}
        <div className=" otp_wrapper input_wrapper">
          <div className="d-flex align-items-center gap-2 ">
            <input
              type="text"
              id="mobile"
              placeholder="Mobile Number"
              disabled={otpVerified}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              {...register("mobile", {
                required: "Mobile number is required",
                pattern: {
                  value: /^\d{10}$/,
                  message: "Enter valid 10-digit mobile number",
                },
              })}
              onInput={(e) => {
                handleChange(e);
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
              onFocus={() => handleFieldFocus("Mobile")}
            />
            {!otpVerified && isVisible && (
              <div className="">
                <button
                  type="button"
                  className="resend"
                  id="sendOtpBtn"
                  onClick={() => {
                    handleSendOtp(!otpSent ? "send" : "resend");

                    //Invmedia Tag for OTP Button
                    if (window.gtag) {
                      window.gtag("event", "conversion", {
                        allow_custom_scripts: true,
                        send_to: "DC-8971752/invmedia/icici01_+unique",
                      });
                    }
                  }}
                  // onClick={() => handleSendOtp(!otpSent ? "send" : "resend")}
                >
                  {!otpSent ? `Send OTP` : `Resend OTP`}
                </button>
              </div>
            )}
          </div>
          {errors.mobile && (
            <span className="error">{errors.mobile.message}</span>
          )}
        </div>

        {/* OTP Field */}
        {otpSent && !otpVerified && timer !== 0 && (
          <div className="otp_wrapper input_wrapper">
            <div className="d-flex align-items-center gap-2 ">
              <input
                type="text"
                id="otp"
                placeholder="Enter the OTP"
                value={otp}
                ref={otpRef}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => {
                  // allow only digits & max 6 chars
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  const maxSix = onlyDigits.slice(0, 6);
                  setOtp(maxSix);
                }}
                onFocus={() => handleFieldFocus("otp")}
              />
              <span className="timer">
                {timer < 10 ? `00:0${timer}` : `00:${timer}`}
              </span>
              {/* Verify OTP button removed – auto verification happens in useEffect */}
            </div>
            {errors.otp && <span className="error">{errors.otp.message}</span>}
          </div>
        )}

        {/* OTP Field
        {otpSent && !otpVerified && timer != 0 && (
          <div className="otp_wrapper input_wrapper">
            <div className="d-flex align-items-center gap-2 ">
              <input
                type="text"
                id="otp"
                placeholder="Enter the OTP"
                value={otp}
                ref={otpRef}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                inputMode="numeric"
                onFocus={() => handleFieldFocus("otp")}
              />
              <span className="timer">
                {timer < 10 ? `00:0${timer}` : `00:${timer}`}
              </span>
              <button
                type="button"
                className="resend"
                id="verifyOtpBtn"
                onClick={handleVerifyOtp}
              >
                Verify OTP
              </button>
            </div>
            {errors.otp && <span className="error">{errors.otp.message}</span>}
          </div>
        )} */}

        <div className="input_wrapper">
          <input
            type="text"
            placeholder="Email ID"
            id="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value:
                  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+(\.com|\.net|\.in|\.edu|\.co\.uk|\.ai|\.org)$/,
                message: "Email is required",
              },
              onChange: (e) => {
                const data = e.target.value;
                handleChange(e);

                // Check allowed domain
                const allowedDomains = [
                  ".com",
                  ".net",
                  ".in",
                  ".edu",
                  ".co.uk",
                  ".ai",
                  ".org",
                ];
                const isDomainAllowed = allowedDomains.some((domain) =>
                  data.endsWith(domain)
                );

                if (isDomainAllowed) {
                  setFocus("pan");
                }
              },
            })}
            onFocus={() => handleFieldFocus("email")}
            onInput={(e) => {
              handleChange(e);
            }}
          />

          {errors.email && (
            <span className="error">{errors.email.message}</span>
          )}
        </div>

        <div className="input_wrapper loan">
          <div className="range-labels pos_rel">
            <label htmlFor="loanAmountValue">Desired Loan Amount</label>
            <input
              type="text"
              id="loanAmountValue"
              name="loanAmountValue"
              className="loanAmountValue"
              min="20000"
              max="2000000"
              onFocus={() => handleFieldFocus("loanAmountValue")}
              value={loanAmountInput} // use input state
              onChange={(e) => {
                // typing behaviour
                const raw = e.target.value.replace(/,/g, "").replace(/\D/g, "");

                // let user clear completely while typing
                setLoanAmountInput(raw);

                if (raw === "") {
                  // don't update loanAmount yet – wait for blur
                  return;
                }

                let val = parseInt(raw, 10);
                if (isNaN(val)) return;

                // update loanAmount (for slider), but we'll clamp again on blur
                if (val < 20000) val = 20000;
                if (val > 2000000) val = 2000000;

                setLoanAmount(val);
                handleNumberChange(e);
                handleChange(e);
              }}
              onBlur={(e) => {
                //  clamp + format when user finishes
                const raw = e.target.value.replace(/,/g, "").replace(/\D/g, "");

                if (raw === "") {
                  setLoanAmount(20000);
                  setLoanAmountInput(formatIndianNumber(20000));
                  return;
                }

                let val = parseInt(raw, 10);
                if (isNaN(val)) {
                  setLoanAmount(20000);
                  setLoanAmountInput(formatIndianNumber(20000));
                  return;
                }

                if (val < 20000) val = 20000;
                if (val > 2000000) val = 2000000;

                setLoanAmount(val);
                setLoanAmountInput(formatIndianNumber(val));
              }}
            />
          </div>

          <input
            type="range"
            min={20000}
            step="20000"
            max={2000000}
            name="loanAmountSlider"
            value={loanAmount}
            {...register("loanAmount")}
            onChange={(e) => {
              const val = Number(e.target.value);
              setLoanAmount(val); // updates main value
              handleSliderChange(e);
              handleChange(e);
              setLoanAmountInput(formatIndianNumber(val)); // sync text with slider
            }}
            onFocus={() => handleFieldFocus("loanAmountSlider")}
            className="custom-slider"
            style={{
              background: `linear-gradient(to right, #e3530f 0%, #e3530f ${percentage}%, #ccc ${percentage}%, #ccc 100%)`,
            }}
          />

          <div className="range-labels">
            <span>₹20,000</span>
            <span>₹20,00,000</span>
          </div>

          {errors.loanAmount && (
            <span className="error">{errors.loanAmount.message}</span>
          )}
        </div>

        {/* <div className="input_wrapper loan">
          <div className="range-labels pos_rel">
            <label htmlFor="loanAmountValue">Desired Loan Amount</label>
            <input
              type="text"
              id="loanAmountValue"
              name="loanAmountValue"
              className="loanAmountValue"
              min="20000"
              max="2000000"
              onFocus={() => handleFieldFocus("loanAmountValue")}
              onChange={(e) => {
                handleNumberChange();
                handleChange(e);
              }}
              value={formatIndianNumber(loanAmount)}
            />
          </div>
          <input
            type="range"
            min="20000"
            step="20000"
            max="2000000"
            name="loanAmountSlider"
            value={loanAmount}
            {...register("loanAmount")}
            onChange={(e) => {
              handleSliderChange(e);
              handleChange(e);
            }}
            onFocus={() => handleFieldFocus("loanAmountSlider")}
            className="custom-slider"
            style={{
              background: `linear-gradient(to right, #e3530f 0%, #e3530f ${percentage}%, #ccc ${percentage}%, #ccc 100%)`,
            }}
          />
          <div className="range-labels">
            <span>₹20,000</span>
            <span>₹20,00,000</span>
          </div>

          {errors.loanAmount && (
            <span className="error">{errors.loanAmount.message}</span>
          )}
        </div> */}

        {/* <button
          type="button"
          className="reset"
          id="resetBtn"
          onClick={handleReset}
        >
          Reset Details
        </button> */}

        <div
          className="position-relative terms input_wrapper d-flex align-items-start justify-content-center mt-3 agreetncwrap"
          style={{ gap: "10px" }}
        >
          <Controller
            name="agreetnc"
            control={control}
            defaultValue={false}
            rules={{
              validate: (value) =>
                value === true || "Please agree to bank’s T&C above",
            }}
            render={({ field }) => (
              <input
                type="checkbox"
                id="agreetnc"
                style={{ width: "fit-content" }}
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  ctaClickEvent(
                    `consent checkbox: ${e.target.checked}`,
                    "lead form",
                    "checkbox",
                    0,
                    "na"
                  );
                }}
              />
            )}
          />
          <label htmlFor="agreetnc">
            I agree ICICI Bank's{" "}
            <a
              target="_blank"
              href="https://www.icicibank.com/terms-condition/cashin-terms-conditions?ITM=nli_cms_cards_terms_condition_cash_in_pl_on_cc_link"
              onClick={() => {
                ctaClickEvent(
                  "terms & conditions",
                  "lead form",
                  "text link",
                  0,
                  "https://www.icicibank.com/terms-condition/cashin-terms-conditions?ITM=nli_cms_cards_terms_condition_cash_in_pl_on_cc_link"
                );
              }}
              onContextMenu={() => {
                ctaClickEvent(
                  "terms & conditions",
                  "lead form",
                  "text link",
                  0,
                  "https://www.icicibank.com/terms-condition/cashin-terms-conditions?ITM=nli_cms_cards_terms_condition_cash_in_pl_on_cc_link"
                );
              }}
            >
              Terms & Conditions for Personal Loan on Credit Card
            </a>
          </label>
        </div>
        {errors.agreetnc && (
          <span className="error">{errors.agreetnc.message}</span>
        )}

        <button
          id="proceed"
          type="submit"
          disabled={disableApplyBtn}
          className="proceed"
          style={{
            backgroundColor: disableApplyBtn ? "grey" : "",
          }}
          onClick={() => {
            //Invmedia Tag for Avail Now Button
            if (window.gtag) {
              window.gtag("event", "conversion", {
                allow_custom_scripts: true,
                send_to: "DC-8971752/invmedia/icici01y+unique",
              });
            }
          }}
        >
          {disableApplyBtn ? "Submitting..." : "Avail Now"}
        </button>
        <div className="toast-container" id="toastContainer"></div>
      </div>
    </form>
  );
}

export default FormComponent;
